import {
	actionPanels,
	editPanels,
	IMapViewerState,
	MapViewerActions,
	MapViewerPanel,
	MapViewerZoomAction
} from './map-viewer.types';
import { produce } from 'immer';
import {
	MAP_VIEWER_DECREASE_ZOOM,
	MAP_VIEWER_INCREASE_ZOOM,
	MAP_VIEWER_SET_ZOOM,
	MAP_VIEWER_SWITCH_ACTION_PANEL,
	MAP_VIEWER_SWITCH_EDIT_PANEL,
	MAP_VIEWER_TOGGLE_PANEL,
	MAP_VIEWER_CLEAR_ACTION_PANEL,
	MAP_VIEWER_CLEAR_EDIT_PANELS
} from './map-viewer.actions';

export const mapViewerInitialState: IMapViewerState = {
	zoom: new Map<string, number>(),
	panels: new Map<MapViewerPanel, boolean>([
		[MapViewerPanel.Drawing, false],
		[MapViewerPanel.DrawSquare, false],
		[MapViewerPanel.DrawCircle, false],
		[MapViewerPanel.Selection, false],
		[MapViewerPanel.Attachement, false],
		[MapViewerPanel.GoogleMaps, false]
	])
};

export function mapViewerReducer(
	state: IMapViewerState = mapViewerInitialState,
	action: MapViewerActions
): IMapViewerState {
	switch (action.type) {
		case MAP_VIEWER_INCREASE_ZOOM:
			return increaseZoom(state, (action as MapViewerZoomAction).mapId);
		case MAP_VIEWER_DECREASE_ZOOM:
			return decreaseZoom(state, (action as MapViewerZoomAction).mapId);
		case MAP_VIEWER_SET_ZOOM:
			return setZoom(
				state,
				(action as MapViewerZoomAction).mapId,
				action.payload as number
			);
		case MAP_VIEWER_TOGGLE_PANEL:
			return togglePanel(
				state,
				action.payload as { panel: MapViewerPanel; open: boolean }
			);
		case MAP_VIEWER_SWITCH_EDIT_PANEL:
			return switchToEditPanel(state, action.payload as MapViewerPanel);
		case MAP_VIEWER_SWITCH_ACTION_PANEL:
			return switchToActionPanel(state, action.payload as MapViewerPanel);
		case MAP_VIEWER_CLEAR_ACTION_PANEL: {
			return clearActionPanel(state);
		}
		case MAP_VIEWER_CLEAR_EDIT_PANELS:
			return clearEditPanels(state);
		default:
			return state;
	}
}

function increaseZoom(
	currentState: IMapViewerState,
	mapId: string
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		const zoomValue: number | undefined = draftState.zoom.get(mapId);
		if (!zoomValue) return;
		draftState.zoom.set(mapId, zoomValue + 0.25);
	});
}

function decreaseZoom(
	currentState: IMapViewerState,
	mapId: string
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		const zoomValue: number | undefined = draftState.zoom.get(mapId);
		if (!zoomValue || zoomValue === 0) return;
		draftState.zoom.set(mapId, zoomValue - 0.25);
	});
}

function setZoom(
	currentState: IMapViewerState,
	mapId: string,
	zoom: number
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		draftState.zoom.set(mapId, zoom);
	});
}

function togglePanel(
	currentState: IMapViewerState,
	panelState: { panel: MapViewerPanel; open: boolean }
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		draftState.panels.set(panelState.panel, panelState.open);
	});
}

function switchToEditPanel(
	currentState: IMapViewerState,
	panel: MapViewerPanel
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		const isActive: boolean = draftState.panels.get(panel) || false;
		editPanels.forEach((editPanel: MapViewerPanel) => {
			if (isActive) {
				draftState.panels.set(editPanel, false);
			} else {
				draftState.panels.set(editPanel, editPanel === panel);
			}
		});
	});
}

function switchToActionPanel(
	currentState: IMapViewerState,
	panel: MapViewerPanel
): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		const isActive: boolean = draftState.panels.get(panel) || false;
		actionPanels.forEach((actionPanel: MapViewerPanel) => {
			if (isActive) {
				draftState.panels.set(actionPanel, false);
			} else {
				draftState.panels.set(actionPanel, actionPanel === panel);
			}
		});
	});
}

function clearActionPanel(currentState: IMapViewerState): IMapViewerState {
	return produce(currentState, (draftState: IMapViewerState) => {
		actionPanels.forEach((actionPanel: MapViewerPanel) => {
			draftState.panels.set(actionPanel, false);
		});
	});
}

function clearEditPanels(currentstate: IMapViewerState): IMapViewerState {
	return produce(currentstate, (draftState: IMapViewerState) => {
		editPanels.forEach((editPanel: MapViewerPanel) => {
			draftState.panels.set(editPanel, false);
		});
	});
}
